// Thermometer and digital clock pictures (14 Oct 2026). A `thermometer` is
// an upright scale with a column of liquid: the reading is drawn against
// the scale and never written or spoken, because reading the scale is the
// question. A `digital_clock` shows its time in digits, exactly as the
// question gives it; a 12-hour clock shows no am or pm.
//
// Pure parsing and geometry only, no components (see
// practice-diagram-data.ts).

export type ThermometerSpec = {
  type: 'thermometer';
  unit: '°C' | '°F';
  min: number;
  max: number;
  // The gap between one mark and the next.
  step: number;
  // Every how many marks carry their number, counting from the bottom.
  labelEvery: number;
  reading: number;
};

export type DigitalClockSpec = {
  type: 'digital_clock';
  time: string;
  mode: '12h' | '24h';
};

// More marks or numbers than this cannot be told apart on a phone.
const MAX_STEPS = 60;
const MAX_NUMBERS = 31;

function isNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isWhole(value: number): boolean {
  return Math.abs(value - Math.round(value)) < 1e-9;
}

export function readThermometer(
  record: Record<string, unknown>,
): ThermometerSpec | null {
  const { unit, min, max, step, reading } = record;
  const labelEvery = record.labelEvery ?? 1;
  if (unit !== '°C' && unit !== '°F') return null;
  if (!isNumber(min) || !isNumber(max) || !isNumber(step)) return null;
  if (!isNumber(reading) || !isNumber(labelEvery)) return null;
  if (step <= 0 || max <= min) return null;
  const steps = (max - min) / step;
  if (!isWhole(steps) || Math.round(steps) > MAX_STEPS) return null;
  if (!Number.isInteger(labelEvery) || labelEvery < 1) return null;
  if (Math.floor(Math.round(steps) / labelEvery) + 1 > MAX_NUMBERS) return null;
  if (reading < min || reading > max) return null;
  // The liquid stops on a mark or half way between two marks: anywhere
  // else could not be read off the scale.
  if (!isWhole((reading - min) / (step / 2))) return null;
  return { type: 'thermometer', unit, min, max, step, labelEvery, reading };
}

const TIME_24 = /^(?:[01]\d|2[0-3]):[0-5]\d$/;
const TIME_12 = /^(?:[1-9]|1[0-2]):[0-5]\d$/;

export function readDigitalClock(
  record: Record<string, unknown>,
): DigitalClockSpec | null {
  const mode = record.mode ?? '24h';
  if (mode !== '12h' && mode !== '24h') return null;
  if (typeof record.time !== 'string') return null;
  const time = record.time.trim();
  // Only a real time, written the way that kind of clock shows it: four
  // digits on a 24-hour clock, no leading zero on a 12-hour clock.
  if (!(mode === '24h' ? TIME_24 : TIME_12).test(time)) return null;
  return { type: 'digital_clock', time, mode };
}

function places(value: number): number {
  return (String(value).split('.')[1] ?? '').length;
}

// A number on the scale, free of the stray digits that adding decimals
// leaves behind.
function tidy(thermometer: ThermometerSpec, value: number): number {
  return Number(
    value.toFixed(Math.max(places(thermometer.min), places(thermometer.step))),
  );
}

function spokenNumber(value: number): string {
  return value < 0 ? `negative ${String(Math.abs(value))}` : String(value);
}

function spokenDegrees(size: number): string {
  return size === 1 ? '1 degree' : `${String(size)} degrees`;
}

// What a screen reader is told in place of the thermometer: its scale, and
// that there is liquid in it. Never the reading, and nothing that places
// it: where the liquid stops is the question.
export function describeThermometer(thermometer: ThermometerSpec): string {
  const { min, max, step, labelEvery } = thermometer;
  const unit =
    thermometer.unit === '°C' ? 'degrees Celsius' : 'degrees Fahrenheit';
  const marks =
    labelEvery === 1
      ? `a mark and a number every ${spokenDegrees(step)}`
      : `a mark every ${spokenDegrees(step)} and a number every ${spokenDegrees(tidy(thermometer, step * labelEvery))}`;
  return `A thermometer marked from ${spokenNumber(min)} to ${spokenNumber(max)} ${unit}, with ${marks}. The liquid has risen part of the way.`;
}

// The digits are the picture, so they are read out as they stand.
export function describeDigitalClock(clock: DigitalClockSpec): string {
  return `A digital clock showing ${clock.time}.`;
}

// What follows is where everything is drawn. The units are close to one
// pixel each when the picture is shown at its natural width.

export type ThermometerTick = {
  value: number;
  y: number;
  // The number beside the mark, with a true minus sign; null for a mark
  // with no number.
  label: string | null;
};

export type ThermometerPicture = {
  ticks: ThermometerTick[];
  // The middle of the tube, and half its width.
  x: number;
  half: number;
  tubeTop: number;
  bulb: { y: number; radius: number };
  // Where the top of the liquid sits.
  liquidTop: number;
  tickFrom: number;
  labelX: number;
  width: number;
  height: number;
};

export const THERMOMETER_LABEL_SIZE = 13;
const TUBE_X = 26;
const TUBE_HALF = 7;
const BULB_RADIUS = 15;
const SCALE_TOP = 40;
const DIGIT_WIDTH = 7.5;

export function thermometerPicture(
  thermometer: ThermometerSpec,
): ThermometerPicture {
  const { min, max, step, labelEvery } = thermometer;
  const steps = Math.round((max - min) / step);
  // Marks sit close together when few of them carry a number, and further
  // apart when every one does, so two numbers never touch.
  const gap = Math.max(8, Math.min(16, Math.ceil(18 / labelEvery)));
  const bottom = SCALE_TOP + steps * gap;
  const yOf = (value: number) => bottom - ((value - min) / step) * gap;
  const ticks: ThermometerTick[] = [];
  for (let index = 0; index <= steps; index += 1) {
    const value = tidy(thermometer, min + index * step);
    ticks.push({
      value,
      y: yOf(value),
      label:
        index % labelEvery === 0
          ? `${value < 0 ? '−' : ''}${String(Math.abs(value))}`
          : null,
    });
  }
  const tickFrom = TUBE_X + TUBE_HALF + 4;
  const labelX = tickFrom + 17;
  const widest = Math.max(
    ...ticks.map((tick) => (tick.label ?? '').length * DIGIT_WIDTH),
  );
  const bulbY = bottom + 12 + BULB_RADIUS;
  return {
    ticks,
    x: TUBE_X,
    half: TUBE_HALF,
    tubeTop: SCALE_TOP - 14,
    bulb: { y: bulbY, radius: BULB_RADIUS },
    liquidTop: yOf(thermometer.reading),
    tickFrom,
    labelX,
    width: labelX + widest + 8,
    height: bulbY + BULB_RADIUS + 6,
  };
}

// Which of the seven strokes of a digit are lit: a is the top, then
// clockwise b to f, and g is the middle.
const SEGMENTS: Record<string, string> = {
  '0': 'abcdef',
  '1': 'bc',
  '2': 'abdeg',
  '3': 'abcdg',
  '4': 'bcfg',
  '5': 'acdfg',
  '6': 'acdefg',
  '7': 'abc',
  '8': 'abcdefg',
  '9': 'abcdfg',
};

export type ClockDigit = {
  digit: string;
  // The corners of each lit stroke, ready for an SVG polygon.
  strokes: string[];
};

export type ClockPicture = {
  digits: ClockDigit[];
  // The two dots between the hours and the minutes.
  dots: { x: number; y: number; size: number }[];
  width: number;
  height: number;
};

const DIGIT = { width: 30, height: 54, thick: 6, gap: 10 };
const COLON = 14;
const EDGE = 16;

function strokeOf(name: string, left: number, top: number): string {
  const { width, height, thick } = DIGIT;
  const half = thick / 2;
  const points: [number, number][] = [];
  if (name === 'a' || name === 'g' || name === 'd') {
    const y = name === 'a' ? half : name === 'g' ? height / 2 : height - half;
    const from = half + 1;
    const to = width - half - 1;
    points.push(
      [from, y],
      [from + half, y - half],
      [to - half, y - half],
      [to, y],
      [to - half, y + half],
      [from + half, y + half],
    );
  } else {
    const x = name === 'f' || name === 'e' ? half : width - half;
    const upper = name === 'f' || name === 'b';
    const from = upper ? half + 1 : height / 2 + 1;
    const to = upper ? height / 2 - 1 : height - half - 1;
    points.push(
      [x, from],
      [x + half, from + half],
      [x + half, to - half],
      [x, to],
      [x - half, to - half],
      [x - half, from + half],
    );
  }
  return points
    .map(([x, y]) => `${String(left + x)},${String(top + y)}`)
    .join(' ');
}

export function clockPicture(clock: DigitalClockSpec): ClockPicture {
  const digits: ClockDigit[] = [];
  const dots: ClockPicture['dots'] = [];
  let left = EDGE;
  for (const mark of clock.time) {
    if (mark === ':') {
      const size = DIGIT.thick;
      for (const part of [0.32, 0.68])
        dots.push({
          x: left + (COLON - size) / 2,
          y: EDGE + DIGIT.height * part - size / 2,
          size,
        });
      left += COLON + DIGIT.gap;
      continue;
    }
    digits.push({
      digit: mark,
      strokes: Array.from(SEGMENTS[mark] ?? '', (name) =>
        strokeOf(name, left, EDGE),
      ),
    });
    left += DIGIT.width + DIGIT.gap;
  }
  return {
    digits,
    dots,
    width: left - DIGIT.gap + EDGE,
    height: DIGIT.height + 2 * EDGE,
  };
}
